import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import TypeListManager from './components/TypeListManager';
import {
  getSubContractors,
  createSubContractor,
  updateSubContractor,
  deleteSubContractor,
  getSubContractorTypes,
} from '../../services/subContractorService';

const emptyForm = () => ({
  name: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  companyAddress: '',
  website: '',
  types: [],
});

const MultiSelectType = ({ options, selected, onChange }) => {
  const toggle = (label) => {
    if (selected?.includes(label)) {
      onChange(selected?.filter(s => s !== label));
    } else {
      onChange([...selected, label]);
    }
  };

  if (options?.length === 0) {
    return <p className="text-xs text-gray-400 italic">No type options defined. Use "Manage Types" to add options.</p>;
  }

  return (
    <div className="flex flex-wrap gap-2">
      {options?.map(opt => (
        <button
          key={opt?.id}
          type="button"
          onClick={() => toggle(opt?.label)}
          className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
            selected?.includes(opt?.label)
              ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-gray-600 border-gray-300 hover:border-orange-400'
          }`}
        >
          {opt?.label}
        </button>
      ))}
    </div>
  );
};

const SubContractorFormModal = ({ item, typeOptions, onSave, onClose, title, saving }) => {
  const [form, setForm] = useState(item ? { ...item } : emptyForm());

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSave = () => {
    if (!form?.name?.trim()) return;
    onSave(form);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded">
            <Icon name="X" size={18} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name <span className="text-red-500">*</span></label>
            <Input type="text" placeholder="Company name" value={form?.name} onChange={(e) => set('name', e?.target?.value)} autoFocus />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
            <Input type="text" placeholder="Primary contact name" value={form?.contactName} onChange={(e) => set('contactName', e?.target?.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
              <Input type="email" placeholder="email@example.com" value={form?.contactEmail} onChange={(e) => set('contactEmail', e?.target?.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Phone</label>
              <Input type="tel" placeholder="(555) 000-0000" value={form?.contactPhone} onChange={(e) => set('contactPhone', e?.target?.value)} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company Address</label>
            <Input type="text" placeholder="123 Main St, City, State ZIP" value={form?.companyAddress} onChange={(e) => set('companyAddress', e?.target?.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
            <Input type="url" placeholder="https://www.example.com" value={form?.website} onChange={(e) => set('website', e?.target?.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
            <MultiSelectType options={typeOptions} selected={form?.types} onChange={(val) => set('types', val)} />
          </div>
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-200">
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={handleSave} disabled={!form?.name?.trim() || saving}>
            {saving ? 'Saving...' : item ? 'Save Changes' : 'Add Sub-Contractor'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const SubContractors = () => {
  const [subContractors, setSubContractors] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showTypeManager, setShowTypeManager] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [subData, typesData] = await Promise.all([
        getSubContractors(),
        getSubContractorTypes(),
      ]);
      setSubContractors(subData);
      setTypeOptions(typesData);
    } catch (err) {
      setError(err?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAdd = async (form) => {
    try {
      setSaving(true);
      const created = await createSubContractor(form);
      setSubContractors(prev => [...prev, created]);
      setShowModal(false);
    } catch (err) {
      setError(err?.message || 'Failed to add sub-contractor');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const handleSaveEdit = async (form) => {
    try {
      setSaving(true);
      const updated = await updateSubContractor(editingItem?.id, form);
      setSubContractors(prev => prev?.map(c => c?.id === editingItem?.id ? updated : c));
      setEditingItem(null);
      setShowModal(false);
    } catch (err) {
      setError(err?.message || 'Failed to update sub-contractor');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteSubContractor(id);
      setSubContractors(prev => prev?.filter(c => c?.id !== id));
    } catch (err) {
      setError(err?.message || 'Failed to delete sub-contractor');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  return (
    <MainLayout>
      <div className="p-6 max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Icon name="HardHat" size={24} className="text-orange-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Sub-Contractors</h1>
              <p className="text-sm text-gray-500">{subContractors?.length} sub-contractor{subContractors?.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setShowTypeManager(prev => !prev)} iconName="Settings" iconPosition="left">
              Manage Types
            </Button>
            <Button variant="primary" size="sm" onClick={() => { setEditingItem(null); setShowModal(true); }} iconName="Plus" iconPosition="left">
              Add Sub-Contractor
            </Button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600"><Icon name="X" size={14} /></button>
          </div>
        )}

        {showTypeManager && (
          <div className="mb-5 bg-white rounded-lg border border-gray-200 shadow-sm p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Sub-Contractor Type Options</h3>
            <TypeListManager types={typeOptions} onTypesChange={setTypeOptions} />
          </div>
        )}

        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="grid grid-cols-12 px-4 py-3 bg-gray-50 border-b border-gray-200 gap-3">
            <span className="col-span-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</span>
            <span className="col-span-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Contact</span>
            <span className="col-span-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Phone</span>
            <span className="col-span-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Type</span>
            <span className="col-span-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Website</span>
            <span className="col-span-1 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Actions</span>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Icon name="Loader2" size={24} className="text-orange-500 animate-spin" />
              <span className="ml-2 text-gray-500">Loading...</span>
            </div>
          ) : subContractors?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Icon name="HardHat" size={40} className="text-gray-300 mb-3" />
              <p className="text-gray-500 font-medium">No sub-contractors yet</p>
              <p className="text-gray-400 text-sm mt-1">Click "Add Sub-Contractor" to get started</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {subContractors?.map((item) => (
                <li key={item?.id} className="grid grid-cols-12 items-center px-4 py-3 hover:bg-gray-50 transition-colors gap-3">
                  <div className="col-span-3">
                    <p className="text-sm font-medium text-gray-900">{item?.name}</p>
                    {item?.companyAddress && <p className="text-xs text-gray-400 truncate">{item?.companyAddress}</p>}
                  </div>
                  <div className="col-span-2">
                    {item?.contactName && <p className="text-sm text-gray-700">{item?.contactName}</p>}
                    {item?.contactEmail && <p className="text-xs text-gray-400 truncate">{item?.contactEmail}</p>}
                  </div>
                  <div className="col-span-2">
                    {item?.contactPhone && <p className="text-sm text-gray-600">{item?.contactPhone}</p>}
                  </div>
                  <div className="col-span-2">
                    <div className="flex flex-wrap gap-1">
                      {item?.types?.map(t => (
                        <span key={t} className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full">{t}</span>
                      ))}
                    </div>
                  </div>
                  <div className="col-span-2">
                    {item?.website && (
                      <a href={item?.website} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline truncate block">
                        {item?.website?.replace(/^https?:\/\//, '')}
                      </a>
                    )}
                  </div>
                  <div className="col-span-1 flex items-center justify-end gap-1">
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(item)} title="Edit">
                      <Icon name="Pencil" size={15} />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(item?.id)} title="Delete" className="text-red-500 hover:text-red-700">
                      <Icon name="Trash2" size={15} />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      {showModal && (
        <SubContractorFormModal
          item={editingItem}
          typeOptions={typeOptions}
          onSave={editingItem ? handleSaveEdit : handleAdd}
          onClose={handleCloseModal}
          title={editingItem ? 'Edit Sub-Contractor' : 'Add Sub-Contractor'}
          saving={saving}
        />
      )}
    </MainLayout>
  );
};

export default SubContractors;
